import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";

export default function AddLanguageModal({
  open,
  onClose,
  availableLanguages,
  addRemoveLanguage,
  currentLanguages = [],
  currentActiveLanguage = "en_US",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [originalSelectedLanguages, setOriginalSelectedLanguages] = useState(
    []
  ); // Track original state
  const [includeAll, setIncludeAll] = useState(false);
  const [activeTab, setActiveTab] = useState("available"); // 'available' or 'selected'
  const [removedEnUS, setRemovedEnUS] = useState(false); // Track if en_US was removed for document clearing

  useEffect(() => {
    setSelectedLanguages(currentLanguages);
    setOriginalSelectedLanguages([...currentLanguages]); // Store original state when modal opens
  }, [currentLanguages]);

  // Reset modal state when it opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setIncludeAll(false);
      setActiveTab("available");
      setSelectedLanguages([...currentLanguages]);
      setOriginalSelectedLanguages([...currentLanguages]);
      setRemovedEnUS(false); // Reset en_US removal flag
    }
  }, [open, currentLanguages]);

  // Update includeAll checkbox state based on current selection
  useEffect(() => {
    if (availableLanguages.length > 0) {
      const allLanguagesSelected = availableLanguages.every((lang) =>
        selectedLanguages.some((selected) => selected.value === lang)
      );
      setIncludeAll(allLanguagesSelected);
    }
  }, [selectedLanguages, availableLanguages]);

  const handleLanguageToggle = (newLang, status) => {
    let newLanguageArray = [...selectedLanguages];
    if (status === "add") {
      newLanguageArray.push({ value: newLang, default: newLang === "en_US" });
      // If adding en_US back, clear the removal flag
      if (newLang === "en_US") {
        setRemovedEnUS(false);
      }
    } else {
      // Prevent removal of currently active language (but allow en_US removal for document clearing)
      if (newLang === currentActiveLanguage) {
        return;
      }

      // Special handling for en_US: remove it from modal but track for document clearing
      if (newLang === "en_US") {
        // Remove en_US from the modal's selected languages
        newLanguageArray = newLanguageArray.filter(
          (lang) => lang.value !== "en_US"
        );
        // Mark that en_US was removed for document clearing
        setRemovedEnUS(true);
      } else {
        // For other languages, remove them completely
        newLanguageArray = newLanguageArray.filter(
          (lang) => lang.value !== newLang
        );
      }
    }
    setSelectedLanguages(newLanguageArray);
  };

  const handleIncludeAllToggle = () => {
    const newIncludeAll = !includeAll;
    setIncludeAll(newIncludeAll);

    if (newIncludeAll) {
      // When checking "Include All", add all available languages to selectedLanguages
      const allLanguages = availableLanguages.map((lang) => ({
        value: lang,
        default: lang === "en_US",
      }));
      setSelectedLanguages(allLanguages);
      setRemovedEnUS(false); // Reset en_US removal flag since we're including all
    } else {
      // When unchecking "Include All", keep only protected languages (currently active language)
      // Always keep en_US as it should always be present
      const protectedLanguages = [];

      // Always include en_US
      protectedLanguages.push({
        value: "en_US",
        default: true,
      });

      // Include currently active language if it's not en_US
      if (currentActiveLanguage !== "en_US") {
        const currentLangExists = protectedLanguages.some(
          (lang) => lang.value === currentActiveLanguage
        );
        if (!currentLangExists) {
          protectedLanguages.push({
            value: currentActiveLanguage,
            default: false,
          });
        }
      }

      setSelectedLanguages(protectedLanguages);
      setRemovedEnUS(false); // Reset en_US removal flag
    }
  };

  const handleSave = () => {
    // Always ensure en_US is included in the final language array
    let finalLanguages = [...selectedLanguages];

    // If en_US was removed from modal, add it back with clearDocuments flag
    if (removedEnUS) {
      finalLanguages.push({
        value: "en_US",
        default: true,
        clearDocuments: true,
      });
    }

    const sendLanguage = finalLanguages.map((lang) => {
      return {
        value: lang.value,
        default: lang.value === "en_US",
        clearDocuments: lang.clearDocuments || false, // Pass the clearDocuments flag
      };
    });
    addRemoveLanguage(sendLanguage);
    // Update original state to current state since we're saving (but clean up clearDocuments flags)
    const cleanedLanguages = finalLanguages.map((lang) => {
      const { clearDocuments, ...cleanLang } = lang;
      return cleanLang;
    });
    setOriginalSelectedLanguages([...cleanedLanguages]);
    // Reset modal state
    setSearchTerm("");
    setIncludeAll(false);
    setActiveTab("available");
    setRemovedEnUS(false);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original state - restore the languages that were selected when modal opened
    setSelectedLanguages([...originalSelectedLanguages]);
    // Reset modal state
    setSearchTerm("");
    setIncludeAll(false);
    setActiveTab("available");
    setRemovedEnUS(false); // Reset en_US removal flag
    // Call addRemoveLanguage to revert any temporary changes in parent component
    const originalLanguageFormat = originalSelectedLanguages.map((lang) => {
      return {
        value: lang.value,
        default: lang.value === "en_US",
      };
    });
    addRemoveLanguage(originalLanguageFormat);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2, minHeight: "500px" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Add Language
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search Language"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2 }}
          size="small"
        />

        {/* Available/Selected Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Box sx={{ display: "flex" }}>
            <Box
              onClick={() => setActiveTab("available")}
              sx={{
                px: 2,
                py: 1,
                borderBottom: 2,
                borderColor:
                  activeTab === "available" ? "primary.main" : "transparent",
                color:
                  activeTab === "available" ? "primary.main" : "text.secondary",
                fontWeight: activeTab === "available" ? 600 : 400,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Available
              <Box
                sx={{
                  minWidth: 24,
                  height: 20,
                  borderRadius: "10px",
                  backgroundColor:
                    activeTab === "available" ? "primary.main" : "grey.300",
                  color: activeTab === "available" ? "white" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  px: 1,
                }}
              >
                {availableLanguages.length - selectedLanguages.length}
              </Box>
            </Box>
            <Box
              onClick={() => setActiveTab("selected")}
              sx={{
                px: 2,
                py: 1,
                borderBottom: 2,
                borderColor:
                  activeTab === "selected" ? "primary.main" : "transparent",
                color:
                  activeTab === "selected" ? "primary.main" : "text.secondary",
                fontWeight: activeTab === "selected" ? 600 : 400,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Selected
              <Box
                sx={{
                  minWidth: 24,
                  height: 20,
                  borderRadius: "10px",
                  backgroundColor:
                    activeTab === "selected" ? "primary.main" : "grey.300",
                  color: activeTab === "selected" ? "white" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  px: 1,
                }}
              >
                {selectedLanguages.length}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Tab Content */}
        {activeTab === "available" ? (
          // Available Languages Content
          <List sx={{ maxHeight: 280, overflow: "auto", py: 0 }}>
            {availableLanguages
              .filter((lang) =>
                lang.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((language) => {
                // Check if this language is currently selected
                const isSelected = selectedLanguages.some(
                  (selected) => selected.value === language
                );
                // Check if this is the currently active/selected language in the main component
                const isCurrentActiveLanguage =
                  language === currentActiveLanguage;
                // Language cannot be removed if it's currently active (but en_US can be "removed" for document clearing)
                const cannotRemove = isCurrentActiveLanguage;

                return (
                  <ListItem
                    key={language}
                    sx={{
                      py: 1,
                      px: 0,
                      borderBottom: "1px solid #f0f0f0",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <span>{language}</span>
                        </Box>
                      }
                      sx={{ flex: 1 }}
                    />

                    <IconButton
                      onClick={() =>
                        handleLanguageToggle(
                          language,
                          isSelected ? "remove" : "add"
                        )
                      }
                      size="small"
                      disabled={isSelected && cannotRemove}
                      sx={{
                        color: isSelected ? "#d32f2f" : "#1976d2",
                        border: "1px solid",
                        borderColor: isSelected ? "#d32f2f" : "#1976d2",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        opacity: isSelected && cannotRemove ? 0.3 : 1,
                      }}
                    >
                      {isSelected ? (
                        <RemoveIcon fontSize="small" />
                      ) : (
                        <AddIcon fontSize="small" />
                      )}
                    </IconButton>
                  </ListItem>
                );
              })}
          </List>
        ) : (
          // Selected Languages Content
          <Box>
            {/* Set as Default and Remove buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mb: 2,
              }}
            >
              <Button
                variant="text"
                size="small"
                color="primary"
                sx={{ textTransform: "none", color: "#2A2C2D" }}
              >
                Set as Default
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                sx={{ textTransform: "none", color: "#2A2C2D" }}
              >
                Remove
              </Button>
            </Box>

            <List sx={{ maxHeight: 280, overflow: "auto", py: 0 }}>
              {selectedLanguages
                .filter((lang) =>
                  lang.value.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((language) => {
                  // Check if this is the currently active/selected language in the main component
                  const isCurrentActiveLanguage =
                    language.value === currentActiveLanguage;
                  // Language cannot be removed if it's currently active (but en_US can be "removed" for document clearing)
                  const cannotRemove = isCurrentActiveLanguage;

                  return (
                    <ListItem
                      key={language.value}
                      sx={{
                        py: 1,
                        px: 0,
                        borderBottom: "1px solid #f0f0f0",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>{language.value}</span>
                          </Box>
                        }
                        sx={{ flex: 1 }}
                      />
                      {/* Show check mark only for the currently active language tab */}
                      {isCurrentActiveLanguage && (
                        <IconButton
                          size="small"
                          sx={{
                            color: "#1976d2",
                            border: "1px solid #1976d2",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            mr: 1,
                            marginRight: "76px !important",
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() =>
                          handleLanguageToggle(language.value, "remove")
                        }
                        size="small"
                        disabled={cannotRemove}
                        sx={{
                          color: "#d32f2f",
                          border: "1px solid #d32f2f",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          opacity: cannotRemove ? 0.3 : 1,
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  );
                })}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Include All Checkbox - Left side - Only show for Available tab */}
        {activeTab === "available" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAll}
                onChange={handleIncludeAllToggle}
                color="primary"
                size="small"
              />
            }
            label="Include All"
          />
        )}

        {/* Spacer when Include All is not shown */}
        {activeTab !== "available" && <Box />}

        {/* Action Buttons - Right side */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={handleCancel} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

AddLanguageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availableLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  addRemoveLanguage: PropTypes.func.isRequired,
  currentLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      default: PropTypes.bool,
    })
  ),
  currentActiveLanguage: PropTypes.string,
};
